import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Trash2, PercentIcon, Settings, ChevronDown, Calculator } from "lucide-react";
import { Button } from "./Button";
import { Card } from "./card";
import { Input } from "./input";
import { Textarea } from "./Textarea";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { 
	CommissionCreateDto, 
	Commission, 
	CommissionBackendResponse,
	CommissionPayoutMethodEnum, 
	CommissionBillingCycleEnum, 
	CommissionCategoryTypeEnum, 
	CommissionStatusEnum,
	CommissionTierNameEnum,
	CommissionTier,
	CommissionRateModifier,
	CommissionRateModifierCreateDto,
	CommissionTierCreateDto
} from "../../modules/shared/model";
import { 
	createCommission, 
	updateCommission, 
	clearCommissionError,
	createCommissionTier,
	updateCommissionTier,
	deleteCommissionTier,
	createCommissionRateModifier,
	updateCommissionRateModifier,
	deleteCommissionRateModifier
} from "../../redux/Slices/commissionSlice";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	commission?: Commission | CommissionBackendResponse | null;
}

const CommissionDialog: React.FC<Props> = ({ isOpen, onClose, commission }) => {
	const dispatch = useAppDispatch();
	const { loading, error } = useAppSelector((s: any) => s.commissions);

	const isEditMode = !!commission;
	
	const [formData, setFormData] = useState<CommissionCreateDto>({
		baseRate: 0,
		name: "",
		status: { id: CommissionStatusEnum.Active },
		payoutMethod: { id: CommissionPayoutMethodEnum.Direct_Deposit },
		billingCycle: { id: CommissionBillingCycleEnum.Monthly },
		categoryType: { id: CommissionCategoryTypeEnum.Local_Service },
		autoAdjustment: true,
		minimumPayout: 0,
	});

	// Separate state for tiers and rate modifiers (not part of commission payload)
	const [commissionTiers, setCommissionTiers] = useState<CommissionTier[]>([
		{
			name: { id: CommissionTierNameEnum.Standard },
			conditions: [],
			rate: 10,
			threshold: 0,
		}
	]);

	const [rateModifiers, setRateModifiers] = useState<CommissionRateModifier[]>([]);

	// Track original data for edit mode to detect changes
	const [originalTiers, setOriginalTiers] = useState<CommissionTier[]>([]);
	const [originalRateModifiers, setOriginalRateModifiers] = useState<CommissionRateModifier[]>([]);

	const [errors, setErrors] = useState<Record<string, string>>({});

	// Helper function to normalize commission data from different sources
	const normalizeCommission = (comm: Commission | CommissionBackendResponse) => {
		const isBackendResponse = "payoutMethod" in comm && !("settings" in comm);
		
		if (isBackendResponse) {
			const backendComm = comm as CommissionBackendResponse;
			return {
				id: backendComm.id,
				baseRate: backendComm.baseRate,
				name: backendComm.name,
				status: { id: parseInt(backendComm.status.id) as CommissionStatusEnum },
				settings: {
					payoutMethod: { id: parseInt(backendComm.payoutMethod.id) as CommissionPayoutMethodEnum },
					billingCycle: { id: parseInt(backendComm.billingCycle.id) as CommissionBillingCycleEnum },
					categoryType: { id: parseInt(backendComm.categoryType.id) as CommissionCategoryTypeEnum },
					autoAdjustment: backendComm.autoAdjustment,
					minimumPayout: backendComm.minimumPayout,
				},
				commissionTier: backendComm.commissionTiers.map(tier => ({
					id: tier.id,
					name: { id: parseInt(tier.name.id) as CommissionTierNameEnum },
					conditions: tier.conditions,
					rate: tier.rate,
					threshold: tier.threshold,
				})),
				rateModifiers: backendComm.commissionRateModifiers.map(modifier => ({
					id: modifier.id,
					commission: { id: modifier.commission.id },
					conditions: modifier.conditions,
					adjustment: modifier.adjustment,
					name: modifier.name,
				})),
			} as Commission;
		}
		
		return comm as Commission;
	};

	// Initialize form data when commission is provided (edit mode)
	useEffect(() => {
		if (commission && isOpen) {
			// Normalize commission data to handle both structures
			const normalizedCommission = normalizeCommission(commission);

			setFormData({
				baseRate: normalizedCommission.baseRate,
				name: normalizedCommission.name,
				status: normalizedCommission.status,
				payoutMethod: normalizedCommission.settings.payoutMethod,
				billingCycle: normalizedCommission.settings.billingCycle,
				categoryType: normalizedCommission.settings.categoryType,
				autoAdjustment: normalizedCommission.settings.autoAdjustment,
				minimumPayout: normalizedCommission.settings.minimumPayout,
			});
			
			const tiers = normalizedCommission.commissionTier || [
				{
					name: { id: CommissionTierNameEnum.Standard },
					conditions: [],
					rate: 10,
					threshold: 0,
				}
			];
			const modifiers = normalizedCommission.rateModifiers || [];
			
			setCommissionTiers(tiers);
			setRateModifiers(modifiers);
			
			// Store original data for comparison
			setOriginalTiers(tiers);
			setOriginalRateModifiers(modifiers);
		} else if (!commission && isOpen) {
			// Reset form for new commission
			setFormData({
				baseRate: 0,
				name: "",
				status: { id: CommissionStatusEnum.Active },
				payoutMethod: { id: CommissionPayoutMethodEnum.Direct_Deposit },
				billingCycle: { id: CommissionBillingCycleEnum.Monthly },
				categoryType: { id: CommissionCategoryTypeEnum.Local_Service },
				autoAdjustment: true,
				minimumPayout: 0,
			});
			const defaultTiers = [
				{
					name: { id: CommissionTierNameEnum.Standard },
					conditions: [],
					rate: 10,
					threshold: 0,
				}
			];
			setCommissionTiers(defaultTiers);
			setRateModifiers([]);
			
			// Clear original data for new commission
			setOriginalTiers([]);
			setOriginalRateModifiers([]);
		}
		setErrors({});
		dispatch(clearCommissionError());
	}, [commission, isOpen, dispatch]);

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.name.trim()) {
			newErrors.name = "Commission name is required";
		}

		if (formData.baseRate < 0 || formData.baseRate > 100) {
			newErrors.baseRate = "Base rate must be between 0 and 100";
		}

		if (formData.minimumPayout < 0) {
			newErrors.minimumPayout = "Minimum payout must be 0 or greater";
		}

		commissionTiers.forEach((tier, index) => {
			if (tier.rate < 0 || tier.rate > 100) {
				newErrors[`tierRate_${index}`] = `Tier ${index + 1} rate must be between 0 and 100`;
			}
			if (tier.threshold < 0) {
				newErrors[`tierThreshold_${index}`] = `Tier ${index + 1} threshold must be 0 or greater`;
			}
		});

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!validateForm()) {
			return;
		}

		try {
			if (isEditMode && commission) {
				// Update main commission
				await dispatch(updateCommission({ id: commission.id, data: formData })).unwrap();
				
				// Handle commission tiers updates
				await handleTierUpdates();
				
				// Handle rate modifier updates
				await handleRateModifierUpdates();
			} else {
				// Create commission first
				const createdCommission = await dispatch(createCommission(formData)).unwrap();
				
				// Create commission tiers if any
				if (commissionTiers && commissionTiers.length > 0) {
					for (const tier of commissionTiers) {
						const tierData: CommissionTierCreateDto = {
							commission: { id: createdCommission.id },
							name: tier.name,
							conditions: tier.conditions,
							rate: tier.rate,
							threshold: tier.threshold,
						};
						await dispatch(createCommissionTier(tierData)).unwrap();
					}
				}
				
				// Create rate modifiers if any
				if (rateModifiers && rateModifiers.length > 0) {
					for (const modifier of rateModifiers) {
						const modifierData: CommissionRateModifierCreateDto = {
							commission: { id: createdCommission.id },
							conditions: modifier.conditions,
							adjustment: modifier.adjustment,
							name: modifier.name,
						};
						await dispatch(createCommissionRateModifier(modifierData)).unwrap();
					}
				}
			}
			onClose();
		} catch (error) {
			console.error("Error saving commission:", error);
		}
	};

	const handleInputChange = (field: string, value: any) => {
		setFormData(prev => ({
			...prev,
			[field]: value,
		}));
		
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors(prev => ({
				...prev,
				[field]: "",
			}));
		}
	};


	const addTier = () => {
		setCommissionTiers(prev => [
			...prev,
			{
				name: { id: CommissionTierNameEnum.Standard },
				conditions: [],
				rate: 10,
				threshold: 0,
			}
		]);
	};

	const removeTier = (index: number) => {
		if (commissionTiers.length > 1) {
			setCommissionTiers(prev => prev.filter((_, i) => i !== index));
		}
	};

	const updateTier = (index: number, field: string, value: any) => {
		setCommissionTiers(prev => prev.map((tier, i) => 
			i === index ? { ...tier, [field]: value } : tier
		));
		
		if (errors[`tierRate_${index}`] || errors[`tierThreshold_${index}`]) {
			setErrors(prev => ({
				...prev,
				[`tierRate_${index}`]: "",
				[`tierThreshold_${index}`]: "",
			}));
		}
	};

	const addCondition = (tierIndex: number) => {
		setCommissionTiers(prev => prev.map((tier, i) => 
			i === tierIndex 
				? { ...tier, conditions: [...tier.conditions, ""] }
				: tier
		));
	};

	const removeCondition = (tierIndex: number, conditionIndex: number) => {
		setCommissionTiers(prev => prev.map((tier, i) => 
			i === tierIndex 
				? { ...tier, conditions: tier.conditions.filter((_, ci) => ci !== conditionIndex) }
				: tier
		));
	};

	const updateCondition = (tierIndex: number, conditionIndex: number, value: string) => {
		setCommissionTiers(prev => prev.map((tier, i) => 
			i === tierIndex 
				? { 
					...tier, 
					conditions: tier.conditions.map((condition, ci) => 
						ci === conditionIndex ? value : condition
					)
				}
				: tier
		));
	};

	// Rate Modifier Functions
	const addRateModifier = () => {
		setRateModifiers(prev => [
			...prev,
			{
				commission: { id: commission?.id || "" },
				conditions: [],
				adjustment: 0,
				name: "",
			}
		]);
	};

	const removeRateModifier = (index: number) => {
		setRateModifiers(prev => prev.filter((_, i) => i !== index));
	};

	const updateRateModifier = (index: number, field: string, value: any) => {
		setRateModifiers(prev => prev.map((modifier, i) => 
			i === index ? { ...modifier, [field]: value } : modifier
		));
	};

	const addRateModifierCondition = (modifierIndex: number) => {
		setRateModifiers(prev => prev.map((modifier, i) => 
			i === modifierIndex 
				? { ...modifier, conditions: [...modifier.conditions, ""] }
				: modifier
		));
	};

	const removeRateModifierCondition = (modifierIndex: number, conditionIndex: number) => {
		setRateModifiers(prev => prev.map((modifier, i) => 
			i === modifierIndex 
				? { ...modifier, conditions: modifier.conditions.filter((_, ci) => ci !== conditionIndex) }
				: modifier
		));
	};

	const updateRateModifierCondition = (modifierIndex: number, conditionIndex: number, value: string) => {
		setRateModifiers(prev => prev.map((modifier, i) => 
			i === modifierIndex 
				? { 
					...modifier, 
					conditions: modifier.conditions.map((condition, ci) => 
						ci === conditionIndex ? value : condition
					)
				}
				: modifier
		));
	};

	// Helper functions to handle tier and rate modifier updates
	const handleTierUpdates = async () => {
		if (!commission) return;

		const currentTiers = commissionTiers;
		const originalTiersData = originalTiers;

		// Find tiers to delete (exist in original but not in current)
		const tiersToDelete = originalTiersData.filter(originalTier => 
			!currentTiers.some(currentTier => 
				// Check if tier has an ID (exists in backend) and matches
				originalTier.id && originalTier.id === currentTier.id
			)
		);

		// Delete removed tiers
		for (const tier of tiersToDelete) {
			if (tier.id) {
				await dispatch(deleteCommissionTier(tier.id)).unwrap();
			}
		}

		// Find tiers to update (exist in both but data changed)
		const tiersToUpdate = currentTiers.filter(currentTier => {
			const originalTier = originalTiersData.find(ot => 
				ot.id && ot.id === currentTier.id
			);
			if (!originalTier) return false;
			
			return (
				originalTier.name.id !== currentTier.name.id ||
				originalTier.rate !== currentTier.rate ||
				originalTier.threshold !== currentTier.threshold ||
				JSON.stringify(originalTier.conditions) !== JSON.stringify(currentTier.conditions)
			);
		});

		// Update changed tiers
		for (const tier of tiersToUpdate) {
			if (tier.id) {
				const updateData = {
					name: tier.name,
					conditions: tier.conditions,
					rate: tier.rate,
					threshold: tier.threshold,
				};
				await dispatch(updateCommissionTier({ id: tier.id, data: updateData })).unwrap();
			}
		}

		// Find tiers to create (new tiers without ID)
		const tiersToCreate = currentTiers.filter(tier => !tier.id);

		// Create new tiers
		for (const tier of tiersToCreate) {
			const tierData: CommissionTierCreateDto = {
				commission: { id: commission.id },
				name: tier.name,
				conditions: tier.conditions,
				rate: tier.rate,
				threshold: tier.threshold,
			};
			await dispatch(createCommissionTier(tierData)).unwrap();
		}
	};

	const handleRateModifierUpdates = async () => {
		if (!commission) return;

		const currentModifiers = rateModifiers;
		const originalModifiersData = originalRateModifiers;

		// Find modifiers to delete (exist in original but not in current)
		const modifiersToDelete = originalModifiersData.filter(originalModifier => 
			!currentModifiers.some(currentModifier => 
				// Check if modifier has an ID (exists in backend) and matches
				originalModifier.id && originalModifier.id === currentModifier.id
			)
		);

		// Delete removed modifiers
		for (const modifier of modifiersToDelete) {
			if (modifier.id) {
				await dispatch(deleteCommissionRateModifier(modifier.id)).unwrap();
			}
		}

		// Find modifiers to update (exist in both but data changed)
		const modifiersToUpdate = currentModifiers.filter(currentModifier => {
			const originalModifier = originalModifiersData.find(om => 
				om.id && om.id === currentModifier.id
			);
			if (!originalModifier) return false;
			
			return (
				originalModifier.name !== currentModifier.name ||
				originalModifier.adjustment !== currentModifier.adjustment ||
				JSON.stringify(originalModifier.conditions) !== JSON.stringify(currentModifier.conditions)
			);
		});

		// Update changed modifiers
		for (const modifier of modifiersToUpdate) {
			if (modifier.id) {
				const updateData = {
					name: modifier.name,
					conditions: modifier.conditions,
					adjustment: modifier.adjustment,
				};
				await dispatch(updateCommissionRateModifier({ id: modifier.id, data: updateData })).unwrap();
			}
		}

		// Find modifiers to create (new modifiers without ID)
		const modifiersToCreate = currentModifiers.filter(modifier => !modifier.id);

		// Create new modifiers
		for (const modifier of modifiersToCreate) {
			const modifierData: CommissionRateModifierCreateDto = {
				commission: { id: commission.id },
				conditions: modifier.conditions,
				adjustment: modifier.adjustment,
				name: modifier.name,
			};
			await dispatch(createCommissionRateModifier(modifierData)).unwrap();
		}
	};

	const getEnumDisplayName = (enumType: any, value: number): string => {
		const enumKey = Object.keys(enumType).find(key => enumType[key] === value);
		return enumKey ? enumKey.replace(/_/g, " ") : "";
	};

	if (!isOpen) return null;

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
			>
				<motion.div
					initial={{ scale: 0.95 }}
					animate={{ scale: 1 }}
					exit={{ scale: 0.95 }}
					className="bg-white rounded-xl max-w-4xl w-full p-6 relative max-h-[90vh] overflow-y-auto"
				>
					<button
						onClick={onClose}
						className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
					>
						<X className="h-5 w-5" />
					</button>

					<div className="pr-8">
						<h2 className="text-2xl font-bold mb-6">
							{isEditMode ? "Edit Commission Rule" : "Add Commission Rule"}
						</h2>

						{error && (
							<div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
								{error}
							</div>
						)}

						<form onSubmit={handleSubmit} className="space-y-6">
							{/* Basic Information */}
							<Card className="p-6">
								<h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
									<Settings className="h-5 w-5" />
									Basic Information
								</h3>
								
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium mb-2">
											Commission Name *
										</label>
										<Input
											type="text"
											value={formData.name}
											onChange={(e) => handleInputChange("name", e.target.value)}
											placeholder="Enter commission name"
											className={errors.name ? "border-red-500" : ""}
										/>
										{errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium mb-2">
											Base Rate (%) *
										</label>
										<Input
											type="number"
											value={formData.baseRate}
											onChange={(e) => handleInputChange("baseRate", parseFloat(e.target.value) || 0)}
											placeholder="0"
											min="0"
											max="100"
											step="0.1"
											className={errors.baseRate ? "border-red-500" : ""}
										/>
										{errors.baseRate && <p className="text-red-500 text-sm mt-1">{errors.baseRate}</p>}
									</div>

									<div>
										<label className="block text-sm font-medium mb-2">
											Status
										</label>
										<select
											value={formData.status.id}
											onChange={(e) => handleInputChange("status", { id: parseInt(e.target.value) })}
											className="w-full p-2 border rounded-lg"
										>
											<option value={CommissionStatusEnum.Active}>Active</option>
											<option value={CommissionStatusEnum.Inactive}>Inactive</option>
										</select>
									</div>

									<div>
										<label className="block text-sm font-medium mb-2">
											Category Type
										</label>
										<select
											value={formData.categoryType.id}
											onChange={(e) => handleInputChange("categoryType", { id: parseInt(e.target.value) })}
											className="w-full p-2 border rounded-lg"
										>
											<option value={CommissionCategoryTypeEnum.Local_Service}>Local Service</option>
											<option value={CommissionCategoryTypeEnum.Professional_Service}>Professional Service</option>
											<option value={CommissionCategoryTypeEnum.International_Service}>International Service</option>
											<option value={CommissionCategoryTypeEnum.Emergency_Service}>Emergency Service</option>
										</select>
									</div>
								</div>
							</Card>

							{/* Settings */}
							<Card className="p-6">
								<h3 className="text-lg font-semibold mb-4">Settings</h3>
								
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium mb-2">
											Payout Method
										</label>
										<select
											value={formData.payoutMethod.id}
											onChange={(e) => handleInputChange("payoutMethod", { id: parseInt(e.target.value) })}
											className="w-full p-2 border rounded-lg"
										>
											<option value={CommissionPayoutMethodEnum.Direct_Deposit}>Direct Deposit</option>
											<option value={CommissionPayoutMethodEnum.Bank_Transfer}>Bank Transfer</option>
											<option value={CommissionPayoutMethodEnum.PayPal}>PayPal</option>
											<option value={CommissionPayoutMethodEnum.Stripe}>Stripe</option>
										</select>
									</div>

									<div>
										<label className="block text-sm font-medium mb-2">
											Billing Cycle
										</label>
										<select
											value={formData.billingCycle.id}
											onChange={(e) => handleInputChange("billingCycle", { id: parseInt(e.target.value) })}
											className="w-full p-2 border rounded-lg"
										>
											<option value={CommissionBillingCycleEnum.Monthly}>Monthly</option>
											<option value={CommissionBillingCycleEnum.Yearly}>Yearly</option>
											<option value={CommissionBillingCycleEnum.Weekly}>Weekly</option>
										</select>
									</div>

									<div>
										<label className="block text-sm font-medium mb-2">
											Minimum Payout ({getDynamicCurrencySymbol()})
										</label>
										<Input
											type="number"
											value={formData.minimumPayout}
											onChange={(e) => handleInputChange("minimumPayout", parseFloat(e.target.value) || 0)}
											placeholder="0"
											min="0"
											step="0.01"
											className={errors.minimumPayout ? "border-red-500" : ""}
										/>
										{errors.minimumPayout && <p className="text-red-500 text-sm mt-1">{errors.minimumPayout}</p>}
									</div>

									<div className="flex items-center gap-2">
										<input
											type="checkbox"
											id="autoAdjustment"
											checked={formData.autoAdjustment}
											onChange={(e) => handleInputChange("autoAdjustment", e.target.checked)}
											className="h-4 w-4"
										/>
										<label htmlFor="autoAdjustment" className="text-sm font-medium">
											Auto Adjustment
										</label>
									</div>
								</div>
							</Card>

							{/* Commission Tiers */}
							<Card className="p-6">
								<div className="flex justify-between items-center mb-4">
									<h3 className="text-lg font-semibold flex items-center gap-2">
										<PercentIcon className="h-5 w-5" />
										Commission Tiers
									</h3>
									<Button
										type="button"
										onClick={addTier}
										className="flex items-center gap-2"
									>
										<Plus className="h-4 w-4" />
										Add Tier
									</Button>
								</div>

								<div className="space-y-4">
									{commissionTiers.map((tier, index) => (
										<div key={index} className="p-4 border rounded-lg">
											<div className="flex justify-between items-start mb-4">
												<h4 className="font-medium">Tier {index + 1}</h4>
												{commissionTiers.length > 1 && (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => removeTier(index)}
														className="text-red-600"
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												)}
											</div>

											<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
												<div>
													<label className="block text-sm font-medium mb-2">
														Tier Name
													</label>
													<select
														value={tier.name.id}
														onChange={(e) => updateTier(index, "name", { id: parseInt(e.target.value) })}
														className="w-full p-2 border rounded-lg"
													>
														<option value={CommissionTierNameEnum.Standard}>Standard</option>
														<option value={CommissionTierNameEnum.Silver}>Silver</option>
														<option value={CommissionTierNameEnum.Gold}>Gold</option>
														<option value={CommissionTierNameEnum.Platinum}>Platinum</option>
													</select>
												</div>

												<div>
													<label className="block text-sm font-medium mb-2">
														Rate (%)
													</label>
													<Input
														type="number"
														value={tier.rate}
														onChange={(e) => updateTier(index, "rate", parseFloat(e.target.value) || 0)}
														placeholder="0"
														min="0"
														max="100"
														step="0.1"
														className={errors[`tierRate_${index}`] ? "border-red-500" : ""}
													/>
													{errors[`tierRate_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`tierRate_${index}`]}</p>}
												</div>

												<div>
													<label className="block text-sm font-medium mb-2">
														Threshold ({getDynamicCurrencySymbol()})
													</label>
													<Input
														type="number"
														value={tier.threshold}
														onChange={(e) => updateTier(index, "threshold", parseFloat(e.target.value) || 0)}
														placeholder="0"
														min="0"
														step="0.01"
														className={errors[`tierThreshold_${index}`] ? "border-red-500" : ""}
													/>
													{errors[`tierThreshold_${index}`] && <p className="text-red-500 text-sm mt-1">{errors[`tierThreshold_${index}`]}</p>}
												</div>
											</div>

											<div>
												<div className="flex justify-between items-center mb-2">
													<label className="block text-sm font-medium">
														Conditions
													</label>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => addCondition(index)}
														className="flex items-center gap-1"
													>
														<Plus className="h-3 w-3" />
														Add Condition
													</Button>
												</div>
												
												<div className="space-y-2">
													{tier.conditions.map((condition, conditionIndex) => (
														<div key={conditionIndex} className="flex gap-2">
															<Input
																type="text"
																value={condition}
																onChange={(e) => updateCondition(index, conditionIndex, e.target.value)}
																placeholder="Enter condition"
																className="flex-1"
															/>
															<Button
																type="button"
																variant="ghost"
																size="sm"
																onClick={() => removeCondition(index, conditionIndex)}
																className="text-red-600"
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</div>
													))}
												</div>
											</div>
										</div>
									))}
								</div>
							</Card>

							{/* Rate Modifiers */}
							<Card className="p-6">
								<div className="flex justify-between items-center mb-4">
									<h3 className="text-lg font-semibold flex items-center gap-2">
										<Calculator className="h-5 w-5" />
										Rate Modifiers
									</h3>
									<Button
										type="button"
										onClick={addRateModifier}
										className="flex items-center gap-2"
									>
										<Plus className="h-4 w-4" />
										Add Modifier
									</Button>
								</div>

								<div className="space-y-4">
									{rateModifiers.map((modifier, index) => (
										<div key={index} className="p-4 border rounded-lg">
											<div className="flex justify-between items-start mb-4">
												<h4 className="font-medium">Rate Modifier {index + 1}</h4>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => removeRateModifier(index)}
													className="text-red-600"
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>

											<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
												<div>
													<label className="block text-sm font-medium mb-2">
														Modifier Name
													</label>
													<Input
														type="text"
														value={modifier.name}
														onChange={(e) => updateRateModifier(index, "name", e.target.value)}
														placeholder="Enter modifier name"
													/>
												</div>

												<div>
													<label className="block text-sm font-medium mb-2">
														Adjustment (%)
													</label>
													<Input
														type="number"
														value={modifier.adjustment}
														onChange={(e) => updateRateModifier(index, "adjustment", parseFloat(e.target.value) || 0)}
														placeholder="0"
														step="0.1"
													/>
												</div>
											</div>

											<div>
												<div className="flex justify-between items-center mb-2">
													<label className="block text-sm font-medium">
														Conditions
													</label>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => addRateModifierCondition(index)}
														className="flex items-center gap-1"
													>
														<Plus className="h-3 w-3" />
														Add Condition
													</Button>
												</div>
												
												<div className="space-y-2">
													{modifier.conditions.map((condition, conditionIndex) => (
														<div key={conditionIndex} className="flex gap-2">
															<Input
																type="text"
																value={condition}
																onChange={(e) => updateRateModifierCondition(index, conditionIndex, e.target.value)}
																placeholder="Enter condition"
																className="flex-1"
															/>
															<Button
																type="button"
																variant="ghost"
																size="sm"
																onClick={() => removeRateModifierCondition(index, conditionIndex)}
																className="text-red-600"
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</div>
													))}
												</div>
											</div>
										</div>
									))}
								</div>
							</Card>

							{/* Form Actions */}
							<div className="flex justify-end gap-3 pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={onClose}
									disabled={loading}
								>
									Cancel
								</Button>
								<Button
									type="submit"
									disabled={loading}
									className="flex items-center gap-2"
								>
									{loading ? "Saving..." : isEditMode ? "Update Commission" : "Create Commission"}
								</Button>
							</div>
						</form>
					</div>
				</motion.div>
			</motion.div>
		</AnimatePresence>
	);
};

export default CommissionDialog;
